import type { Status } from '../../shared/pruefpunkte.js';
import { modus } from '../api.js';
import { abschnitt, h, knopf, liste, meldung, schritte, setzeMeldung, zeile } from '../dom.js';
import { ergebnisVon, melde } from '../ergebnisse.js';
import type { Ansicht } from '../router.js';
import { formatiereDatum, NB } from '../zeit.js';
import { Aufraeumer, kopf, pruefpunktStand } from './gemeinsam.js';

/**
 * Tastatur (Konzept, Bedienung: „Zurück und Tastatur wie erwartet“): Ist die Tastatur
 * offen, verschwindet die untere Leiste, der Speichern-Knopf sitzt über der Tastatur,
 * und das aktive Feld bleibt sichtbar. Die Ansicht baut das so, wie TFM es vorhat:
 * Die Tastatur erkennt sie daran, dass der sichtbare Bereich (visualViewport)
 * deutlich kleiner wird, und schiebt die Speichern-Leiste an dessen Unterkante.
 * Gemessen wird nach jedem Antippen eines Felds, sobald die Lage ruhig ist; nach
 * bloßem Scrollen nicht, denn dann darf das Feld aus dem Bild wandern.
 */

/** Ab so viel weniger sichtbarer Höhe gilt die Tastatur als offen (Handy-Tastaturen haben über 250 px). */
const TASTATUR_AB_PX = 120;
/** So lange ohne Änderung, dann gilt die Lage als ruhig (die Tastatur fährt in etwa 250 ms auf). */
const RUHE_MS = 400;
/** Rundungen der Geräte (Bruchteile von Pixeln) zählen nicht als verdeckt. */
const TOLERANZ_PX = 2;
/** Abstand zum Rand, wenn die Ansicht ein Feld selbst hervorholt */
const RAND_PX = 8;

const FELDER = [
  { id: 'tastatur-anlage', name: 'Anlage' },
  { id: 'tastatur-zaehler', name: 'Zählerstand' },
  { id: 'tastatur-bemerkung', name: 'Bemerkung' },
] as const;
type FeldId = (typeof FELDER)[number]['id'];

type Beobachtung = 'alles' | 'gesprungen' | 'knopf-verdeckt' | 'feld-verdeckt';

interface Messung {
  feld: FeldId;
  tastaturPx: number;
  speichern: boolean;
  feldSichtbar: boolean;
  feldFrei: boolean;
  untereLeiste: boolean;
  kopfleiste: boolean;
  nachgeholfen: boolean;
  innenPx: number;
}

interface Bereich {
  oben: number;
  unten: number;
}

function ganzDrin(r: DOMRect, b: Bereich): boolean {
  return r.height > 0 && r.top >= b.oben - TOLERANZ_PX && r.bottom <= b.unten + TOLERANZ_PX;
}

function teilsDrin(r: DOMRect, b: Bereich): boolean {
  return r.height > 0 && r.bottom > b.oben + TOLERANZ_PX && r.top < b.unten - TOLERANZ_PX;
}

function ueberlappt(a: DOMRect, b: DOMRect): boolean {
  return a.bottom > b.top + TOLERANZ_PX && a.top < b.bottom - TOLERANZ_PX && a.right > b.left && a.left < b.right;
}

function feldName(id: FeldId): string {
  return FELDER.find((f) => f.id === id)?.name ?? id;
}

function maengel(m: Messung): string[] {
  return [
    !m.speichern && 'Speichern-Knopf verdeckt',
    !m.feldSichtbar && 'Feld nicht ganz sichtbar',
    !m.feldFrei && 'Feld unter einer Leiste',
    m.untereLeiste && 'untere Leiste sichtbar',
  ].filter((t): t is string => typeof t === 'string');
}

function mal(anzahl: number): string {
  return anzahl === 1 ? '1-mal' : `${anzahl}-mal`;
}

export const tastatur: Ansicht = async (ziel) => {
  const aufraeumer = new Aufraeumer();
  const vv = window.visualViewport ?? undefined;
  const kopfleiste = document.querySelector<HTMLElement>('.kopf');
  const statusFrage = meldung();
  // Rückmeldungen stehen in der Speichern-Leiste: Nur sie ist beim Tippen sicher zu sehen.
  const anzeige = h('p', { class: 'speichern-messung', role: 'status' }, vv ? 'Noch keine Messung.' : 'Kein visualViewport.');
  const grundInnen = window.innerHeight;
  /** Sichtbare Höhe ohne Tastatur; gilt für die Breite, bei der sie gemessen wurde (nach dem Drehen neu). */
  let grundHoehe = vv?.height ?? 0;
  let grundBreite = window.innerWidth;
  /** Sichtbare Höhe bei der letzten ruhigen Lage; nur ein Sprung um eine Tastaturhöhe löst eine neue Messung aus. */
  let hoeheZuletzt = vv?.height ?? 0;
  let messungen: Messung[] = [];
  const angetippt = new Set<FeldId>();
  /** Nach dem Antippen eines Felds oder wenn die Tastatur auf- oder zugeht, zählt die nächste ruhige Lage als Messung. */
  let messungFaellig = false;
  let nachgeholfen = false;
  let ruhe: number | undefined;
  let rahmen: number | undefined;
  let getipptMitTastatur: boolean | undefined;

  const anlage = h('input', { id: 'tastatur-anlage', name: 'anlage', type: 'text', autocomplete: 'off', enterkeyhint: 'next', 'aria-describedby': 'tastatur-anlage-hilfe' });
  const zaehler = h('input', { id: 'tastatur-zaehler', name: 'zaehlerstand', type: 'text', inputmode: 'decimal', autocomplete: 'off', enterkeyhint: 'next' });
  const bemerkung = h('textarea', { id: 'tastatur-bemerkung', name: 'bemerkung', rows: 3, autocomplete: 'off' });
  const felder: HTMLElement[] = [anlage, zaehler, bemerkung];

  const speichernKnopf = h('button', { type: 'submit', class: 'knopf knopf-primaer' }, 'Speichern');
  const speichernLeiste = h('div', { class: 'speichern-leiste' }, anzeige, speichernKnopf);
  const untereLeiste = h(
    'nav',
    { class: 'untere-leiste', 'aria-label': 'Untere Leiste (Attrappe)' },
    ...['Übersicht', 'Scannen', 'Mehr'].map((text) =>
      h(
        'button',
        {
          type: 'button',
          class: 'leisten-knopf',
          onclick: () => {
            anzeige.textContent = 'Nur eine Attrappe: So sitzt die untere Leiste später in TFM.';
          },
        },
        text,
      ),
    ),
  );
  /** Unsichtbarer Punkt an der Unterkante des Layout-Viewports, an der feste Leisten sitzen */
  const messpunkt = h('div', { class: 'tastatur-messpunkt', 'aria-hidden': 'true' });

  const aktivesFeld = (): HTMLElement | undefined => felder.find((f) => f === document.activeElement);
  const tastaturPx = (): number => (vv ? Math.max(0, grundHoehe - vv.height) : 0);
  // Beim Zoomen mit zwei Fingern schrumpft der sichtbare Bereich auch; das ist keine Tastatur. Ohne Bedingung
  // an den Fokus: Nach dem Antippen von „Speichern“ fährt die Leiste so mit der Tastatur hinunter, statt vorher wegzuspringen.
  const tastaturOffen = (): boolean => !!vv && Math.abs(vv.scale - 1) < 0.05 && tastaturPx() >= TASTATUR_AB_PX;
  const sichtbar = (): Bereich => (vv ? { oben: vv.offsetTop, unten: vv.offsetTop + vv.height } : { oben: 0, unten: window.innerHeight });

  /** Untere Leiste aus, Speichern-Leiste an die Unterkante des sichtbaren Bereichs, also über die Tastatur. */
  const positioniere = (): void => {
    rahmen = undefined;
    const offen = tastaturOffen();
    // Erst messen, dann ändern: So erzwingt die Ansicht kein zusätzliches Layout.
    const abstand = offen ? Math.max(0, messpunkt.getBoundingClientRect().top - sichtbar().unten) : 0;
    ansicht.classList.toggle('tastatur-offen', offen);
    speichernLeiste.style.transform = abstand > 0 ? `translateY(${-abstand}px)` : '';
  };

  /** Der Teil des sichtbaren Bereichs, den keine Leiste bedeckt */
  const freierBereich = (): Bereich => {
    const b = sichtbar();
    let { oben, unten } = b;
    const kopfR = kopfleiste?.getBoundingClientRect();
    if (kopfR && teilsDrin(kopfR, b)) oben = Math.max(oben, kopfR.bottom);
    for (const leiste of [speichernLeiste, untereLeiste]) {
      const r = leiste.getBoundingClientRect();
      if (teilsDrin(r, b)) unten = Math.min(unten, r.top);
    }
    return { oben, unten };
  };

  const holeHervor = (feld: HTMLElement): void => {
    const r = feld.getBoundingClientRect();
    const frei = freierBereich();
    if (r.top < frei.oben || r.height > frei.unten - frei.oben) window.scrollBy(0, r.top - frei.oben - RAND_PX);
    else if (r.bottom > frei.unten) window.scrollBy(0, r.bottom - frei.unten + RAND_PX);
  };

  const messe = (feld: HTMLElement): Messung => {
    const b = sichtbar();
    const r = feld.getBoundingClientRect();
    const leisteR = untereLeiste.getBoundingClientRect();
    const kopfR = kopfleiste?.getBoundingClientRect();
    const decken = [speichernLeiste.getBoundingClientRect(), leisteR, kopfR].filter((l): l is DOMRect => !!l && teilsDrin(l, b));
    return {
      feld: feld.id as FeldId,
      tastaturPx: Math.round(tastaturPx()),
      speichern: ganzDrin(speichernKnopf.getBoundingClientRect(), b),
      feldSichtbar: ganzDrin(r, b),
      feldFrei: !decken.some((l) => ueberlappt(r, l)),
      untereLeiste: teilsDrin(leisteR, b),
      kopfleiste: !!kopfR && teilsDrin(kopfR, b),
      nachgeholfen,
      innenPx: window.innerHeight,
    };
  };

  const beiRuhe = (): void => {
    positioniere();
    if (!vv) return;
    const feld = aktivesFeld();
    // Kleine Änderungen (Adressleiste beim Scrollen) lösen keine Messung aus: Dann darf das Feld aus dem Bild wandern.
    const sprung = Math.abs(vv.height - hoeheZuletzt) >= TASTATUR_AB_PX / 2;
    hoeheZuletzt = vv.height;
    if (!feld) {
      // Ohne Fokus ist die Tastatur zu. Nach dem Drehen oder wenn das Fenster deutlich niedriger wird
      // (geteilter Bildschirm), gilt die neue Höhe; sonst die größte bisher gesehene, denn die Leiste
      // des Browsers kann den sichtbaren Bereich etwas verkleinern.
      if (Math.abs(vv.scale - 1) >= 0.05) return;
      if (window.innerWidth !== grundBreite || grundHoehe - vv.height >= TASTATUR_AB_PX) {
        grundBreite = window.innerWidth;
        grundHoehe = vv.height;
      } else {
        grundHoehe = Math.max(grundHoehe, vv.height);
      }
      return;
    }
    if (sprung) {
      messungFaellig = true;
      nachgeholfen = false;
    }
    if (!messungFaellig) return;
    if (!tastaturOffen()) {
      // Geht die Tastatur noch auf, löst ihr Sprung die Messung später aus.
      messungFaellig = false;
      anzeige.textContent = `${feldName(feld.id as FeldId)}: Tastatur nicht erkannt.`;
      return;
    }
    const m = messe(feld);
    // Liegt das Feld unter einer Leiste oder außerhalb, holt die Ansicht es einmal hervor, wie TFM es täte, und misst dann neu.
    if ((!m.feldSichtbar || !m.feldFrei) && !nachgeholfen) {
      nachgeholfen = true;
      holeHervor(feld);
      plane();
      return;
    }
    messungFaellig = false;
    messungen.push(m);
    const fehlt = maengel(m);
    anzeige.textContent = `Messung ${messungen.length}, ${feldName(m.feld)}: ${fehlt.length ? fehlt.join(', ') : 'alles sichtbar'}.`;
  };

  function plane(): void {
    rahmen ??= requestAnimationFrame(positioniere);
    window.clearTimeout(ruhe);
    ruhe = window.setTimeout(beiRuhe, RUHE_MS);
  }

  const werteAus = async (): Promise<void> => {
    const mitTastatur = getipptMitTastatur === true;
    getipptMitTastatur = undefined;
    if (!vv) {
      await melde('tastatur-leisten', 'nicht_unterstuetzt', 'Der Browser kennt kein visualViewport; die Tastatur lässt sich nicht erkennen.', { modus: modus() });
      return;
    }
    const nichtAngetippt = FELDER.filter((f) => !angetippt.has(f.id));
    if (nichtAngetippt.length > 0) {
      anzeige.textContent = `Erst in alle drei Felder tippen. Es fehlt: ${nichtAngetippt.map((f) => `„${f.name}“`).join(', ')}.`;
      return;
    }
    const basis = { modus: modus(), grundHoehe: Math.round(grundHoehe), grundInnen, virtualKeyboard: 'virtualKeyboard' in navigator };
    if (messungen.length === 0) {
      await melde(
        'tastatur-leisten',
        'fehler',
        'Tastatur nicht erkannt: Der sichtbare Bereich (visualViewport) wurde beim Tippen nicht kleiner. Die untere Leiste bliebe dann stehen.',
        { ...basis, sichtbarPx: Math.round(vv.height), innenPx: window.innerHeight, mitOffenerTastaturGespeichert: mitTastatur },
      );
      anzeige.textContent = 'Gespeichert. Jetzt unten die Frage beantworten.';
      angetippt.clear();
      return;
    }
    const ohneMessung = FELDER.filter((f) => !messungen.some((m) => m.feld === f.id));
    if (ohneMessung.length > 0) {
      anzeige.textContent = `Noch keine Messung bei offener Tastatur für ${ohneMessung.map((f) => `„${f.name}“`).join(', ')}. Dort tippen und kurz warten.`;
      return;
    }
    const zaehle = (bedingung: (m: Messung) => boolean): number => messungen.filter(bedingung).length;
    const felderMit = (bedingung: (m: Messung) => boolean): string => [...new Set(messungen.filter(bedingung).map((m) => feldName(m.feld)))].join(', ');
    const fehler = {
      speichernVerdeckt: zaehle((m) => !m.speichern),
      feldNichtSichtbar: zaehle((m) => !m.feldSichtbar),
      feldUnterLeiste: zaehle((m) => !m.feldFrei),
      untereLeisteSichtbar: zaehle((m) => m.untereLeiste),
    };
    const teile = [
      fehler.speichernVerdeckt > 0 && `Speichern-Knopf ${mal(fehler.speichernVerdeckt)} verdeckt`,
      fehler.feldNichtSichtbar > 0 && `Feld ${mal(fehler.feldNichtSichtbar)} nicht ganz sichtbar (${felderMit((m) => !m.feldSichtbar)})`,
      fehler.feldUnterLeiste > 0 && `Feld ${mal(fehler.feldUnterLeiste)} unter einer Leiste (${felderMit((m) => !m.feldFrei)})`,
      fehler.untereLeisteSichtbar > 0 && `untere Leiste ${mal(fehler.untereLeisteSichtbar)} sichtbar`,
    ].filter((t): t is string => typeof t === 'string');
    const tastaturMax = Math.max(...messungen.map((m) => m.tastaturPx));
    const umfang = `${FELDER.length} Felder, ${messungen.length} Messungen, Tastatur etwa ${tastaturMax}${NB}px`;
    const gespeichert = mitTastatur ? ' Mit offener Tastatur gespeichert.' : '';
    const ergebnis: Status = teile.length === 0 ? 'ok' : 'fehler';
    const text =
      ergebnis === 'ok'
        ? `Bei offener Tastatur blieben Speichern-Knopf und Feld sichtbar, die untere Leiste war weg (${umfang}).${gespeichert}`
        : `Bei offener Tastatur: ${teile.join(', ')} (${umfang}).${gespeichert}`;
    await melde('tastatur-leisten', ergebnis, text, {
      ...basis,
      messungen: messungen.length,
      fehler,
      tastaturPx: tastaturMax,
      kopfleisteSichtbar: zaehle((m) => m.kopfleiste),
      nachgeholfen: zaehle((m) => m.nachgeholfen),
      layoutViewportSchrumpft: messungen.some((m) => grundInnen - m.innenPx >= TASTATUR_AB_PX),
      mitOffenerTastaturGespeichert: mitTastatur,
      ersteMitMangel: messungen.find((m) => maengel(m).length > 0) ?? null,
    });
    messungen = [];
    angetippt.clear();
    anzeige.textContent = 'Gespeichert. Jetzt unten die Frage beantworten.';
  };

  const formular = h(
    'form',
    { class: 'formular', novalidate: true, 'aria-label': 'Testformular' },
    h(
      'div',
      { class: 'feld' },
      h('label', { for: 'tastatur-anlage' }, 'Anlage'),
      anlage,
      h('p', { id: 'tastatur-anlage-hilfe', class: 'hilfe' }, 'Beliebiger Text, etwa „Kältemaschine 3“.'),
    ),
    h('div', { class: 'feld' }, h('label', { for: 'tastatur-zaehler' }, 'Zählerstand in kWh'), zaehler),
    liste(zeile('Standort', 'Technikraum, 2. UG'), zeile('Letzte Prüfung', formatiereDatum('2026-03-12T12:00:00Z')), zeile('Nächste Prüfung', formatiereDatum('2027-03-12T12:00:00Z'))),
    h('div', { class: 'feld' }, h('label', { for: 'tastatur-bemerkung' }, 'Bemerkung'), bemerkung),
    speichernLeiste,
  );
  formular.addEventListener('submit', (ereignis) => {
    ereignis.preventDefault();
    void werteAus();
  });
  // „Weiter“ auf der Tastatur springt ins nächste Feld, statt das Formular abzuschicken.
  formular.addEventListener('keydown', (ereignis) => {
    if (ereignis.key !== 'Enter' || ereignis.isComposing || !(ereignis.target instanceof HTMLInputElement)) return;
    ereignis.preventDefault();
    felder[felder.indexOf(ereignis.target) + 1]?.focus();
  });
  formular.addEventListener('focusin', (ereignis) => {
    const feld = felder.find((f) => f === ereignis.target);
    if (!feld) return;
    angetippt.add(feld.id as FeldId);
    messungFaellig = true;
    nachgeholfen = false;
    plane();
  });
  formular.addEventListener('focusout', () => plane());
  // Vor dem Antippen festhalten, ob die Tastatur offen war; danach geht sie am Handy meist zu.
  speichernKnopf.addEventListener('pointerdown', () => {
    getipptMitTastatur = tastaturOffen();
  });
  if (vv) {
    aufraeumer.hoere(vv, 'resize', () => plane());
    aufraeumer.hoere(vv, 'scroll', () => plane());
  }
  aufraeumer.hoere(window, 'resize', () => plane());
  aufraeumer.dazu(() => {
    window.clearTimeout(ruhe);
    if (rahmen !== undefined) cancelAnimationFrame(rahmen);
  });

  const antworte = async (beobachtung: Beobachtung): Promise<void> => {
    const gemessen = ergebnisVon('tastatur-leisten');
    const antwortDetails = { beobachtet: beobachtung, gemessen: gemessen?.modus === modus() ? gemessen.status : null, modus: modus(), quelle: 'von Hand' };
    const [ergebnis, text]: [Status, string] =
      beobachtung === 'alles'
        ? ['ok', 'Speichern-Knopf und Feld blieben beim Tippen sichtbar (laut Angabe).']
        : beobachtung === 'gesprungen'
          ? ['ok', 'Am Ende alles sichtbar, die Leisten sprangen aber kurz (laut Angabe).']
          : beobachtung === 'knopf-verdeckt'
            ? ['fehler', 'Der Speichern-Knopf war bei offener Tastatur verdeckt oder weg (laut Angabe).']
            : ['fehler', 'Eine Leiste oder die Tastatur lag über dem Feld (laut Angabe).'];
    await melde('tastatur-beobachtung', ergebnis, text, antwortDetails);
    setzeMeldung(statusFrage, 'Gespeichert.', 'ok');
  };

  const ansicht = h(
    'div',
    { class: 'tastatur-ansicht' },
    ...kopf('Tastatur', 'Bleiben bei offener Tastatur der Speichern-Knopf und das Feld sichtbar, und verschwindet die untere Leiste? (Konzept, Bedienung)'),
    abschnitt(
      'Formular',
      pruefpunktStand('tastatur-leisten', aufraeumer),
      schritte(
        'In der installierten App prüfen, das Handy hochkant; danach gern auch im Browser.',
        'Nacheinander in alle drei Felder tippen und jeweils ein paar Zeichen eingeben. Mit „Weiter“ auf der Tastatur geht es ins nächste Feld.',
        'Beobachten: Die untere Leiste soll verschwinden, „Speichern“ direkt über der Tastatur sitzen und das Feld frei bleiben.',
        '„Speichern“ antippen, solange die Tastatur noch offen ist. Die App wertet ihre Messungen dann aus.',
        'Unten die Frage beantworten.',
      ),
      formular,
    ),
    abschnitt(
      'Frage',
      pruefpunktStand('tastatur-beobachtung', aufraeumer),
      h(
        'div',
        { class: 'frage', role: 'group', 'aria-labelledby': 'frage-tastatur' },
        h('p', { id: 'frage-tastatur' }, 'Blieben beim Tippen der Speichern-Knopf und das Feld sichtbar?'),
        knopf('Ja, alles sichtbar', () => antworte('alles')),
        knopf('Ja, aber es sprang kurz', () => antworte('gesprungen'), 'sekundaer'),
        knopf('Speichern-Knopf verdeckt', () => antworte('knopf-verdeckt'), 'sekundaer'),
        knopf('Feld verdeckt', () => antworte('feld-verdeckt'), 'sekundaer'),
      ),
      statusFrage,
    ),
    abschnitt(
      'Was der Browser meldet',
      liste(
        zeile('visualViewport', vv ? 'ja' : 'nein'),
        zeile('Sichtbare Höhe ohne Tastatur', vv ? `${Math.round(grundHoehe)}${NB}px` : '–'),
        zeile('VirtualKeyboard API', 'virtualKeyboard' in navigator ? 'ja' : 'nein'),
        zeile('Läuft als', modus() === 'app' ? 'installierte App' : 'Browser'),
      ),
    ),
    untereLeiste,
    messpunkt,
  );
  ziel.append(ansicht);
  positioniere();
  return aufraeumer.alles;
};
